/* variant-media-filter.js (Spotlight thumbnail carousel version)
   - Filters product media + thumbnail carousel items by Color variant
   - Uses image ALT tags: "Color: Red", "Color: Blue", "Color: All"
   - Avoids triggering fullscreen/lightbox
   - Minimizes flash by hiding gallery briefly during swap
*/

(() => {
  const OPTION_NAME = 'Color';
  const DEBUG = false;

  const log = (...a) => DEBUG && console.log('[variant-media-filter]', ...a);
  const norm = (s) => (s || '').toString().trim().toLowerCase();

  // Parse "Color: Red" anywhere in the alt text
  function parseColorFromAlt(alt) {
    const m = (alt || '').match(/color\s*:\s*([a-z0-9 ]+)/i);
    return m?.[1] ? norm(m[1]) : null; // "red" | "blue" | "all" | null
  }

  // Detect selected color from select or radios
  function getSelectedColor(scope = document) {
    const sel = scope.querySelector(`select[name="options[${OPTION_NAME}]"]`);
    if (sel?.value) return norm(sel.value);

    const r =
      scope.querySelector(`input[type="radio"][name="options[${OPTION_NAME}]"]:checked`) ||
      scope.querySelector(`input[type="radio"][name="${OPTION_NAME}"]:checked`) ||
      scope.querySelector(`input[type="radio"][name*="Color"]:checked`);
    if (r?.value) return norm(r.value);

    return null;
  }

  // Restrict work to the current product section when possible
  function getRootFromTarget(t) {
    return t?.closest?.('.shopify-section') || document;
  }

  // Create a tiny CSS helper to prevent "flash"
  function ensureFlashStyle() {
    if (document.getElementById('vmf-style')) return;
    const style = document.createElement('style');
    style.id = 'vmf-style';
    style.textContent = `
      html.vmf-filtering .product__media-list,
      html.vmf-filtering .thumbnail-list,
      html.vmf-filtering .thumbnail-slider,
      html.vmf-filtering media-gallery {
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Spotlight/Dawn-like media containers
  function getMediaContainers(root) {
    const candidates = [
      ...root.querySelectorAll('[data-media-id]'),
      ...root.querySelectorAll('.product__media-item'),
      ...root.querySelectorAll('.product__media-list > li'),
      ...root.querySelectorAll('media-gallery li')
    ];

    // Keep only those that actually contain visible media
    return [...new Set(candidates)].filter(el => el.querySelector('img, video, model-viewer, iframe'));
  }

  // Spotlight thumbnail carousel items
  function getThumbContainers(root) {
    const candidates = [
      ...root.querySelectorAll('.thumbnail-list__item'),
      ...root.querySelectorAll('[data-thumbnail-id]'),
      ...root.querySelectorAll('[data-target]'),
      ...root.querySelectorAll('.thumbnail-slider .slider__slide')
    ];

    return [...new Set(candidates)].filter(el => el.querySelector('img, button, a'));
  }

  function getAltFromContainer(el) {
    const img = el.querySelector('img[alt]');
    if (img) return img.getAttribute('alt') || '';
    return el.getAttribute('aria-label') || el.getAttribute('data-alt') || '';
  }

  // Hiding strategy:
  // - Use display:none to truly remove from carousel
  // - Also set hidden/aria-hidden for accessibility
  function setHidden(el, hide) {
    if (!el) return;
    el.style.display = hide ? 'none' : '';
    el.toggleAttribute('hidden', !!hide);
    el.setAttribute('aria-hidden', hide ? 'true' : 'false');
  }

  // Force thumbnail carousel to recalc after we hide/show items
  function refreshCarousels(root) {
    // Dawn/Spotlight uses <slider-component> custom element in many cases
    const sliders = root.querySelectorAll('slider-component');
    sliders.forEach(sl => {
      try {
        if (typeof sl.resetPages === 'function') sl.resetPages();
        if (typeof sl.initPages === 'function') sl.initPages();
        if (typeof sl.update === 'function') sl.update();
      } catch (e) {}
    });

    // Generic: many carousels respond to resize
    window.dispatchEvent(new Event('resize'));
  }

  function filterContainers(containers, selected) {
    let tagged = 0;

    containers.forEach(el => {
      const alt = getAltFromContainer(el);
      const tag = parseColorFromAlt(alt); // null | "red" | "all" etc.

      if (!tag) {
        // Untagged = show for all colors
        setHidden(el, false);
        return;
      }

      tagged++;
      const show = (tag === 'all' || tag === selected);
      setHidden(el, !show);
    });

    return tagged;
  }

  function applyFilter(root) {
    const selected = getSelectedColor(root) || getSelectedColor(document);
    if (!selected) return;

    const media = getMediaContainers(root);
    const thumbs = getThumbContainers(root);

    const taggedMedia = filterContainers(media, selected);
    const taggedThumbs = filterContainers(thumbs, selected);

    log('selected', selected, 'media', media.length, 'thumbs', thumbs.length, 'tagged', taggedMedia, taggedThumbs);

    // If nothing is tagged, do nothing (prevents accidental blanking)
    if (taggedMedia + taggedThumbs === 0) return;

    refreshCarousels(root);
  }

  // Double-pass: immediate + next paint (reduces flash)
  function applyNoFlash(root) {
    ensureFlashStyle();
    document.documentElement.classList.add('vmf-filtering');

    applyFilter(root);
    requestAnimationFrame(() => {
      applyFilter(root);
      document.documentElement.classList.remove('vmf-filtering');
    });
  }

  // Detect if a change event came from the Color option
  function isColorControl(t) {
    if (!t) return false;
    if (!(t.matches?.('select, input[type="radio"]'))) return false;

    const name = (t.getAttribute('name') || '');
    if (name === `options[${OPTION_NAME}]`) return true;
    if (name.includes(`options[${OPTION_NAME}]`)) return true;
    if (name.toLowerCase().includes('color')) return true;
    return false;
  }

  function bind() {
    console.log('[variant-media-filter] loaded');

    // Initial pass
    applyNoFlash(document);

    // IMPORTANT: only respond to CHANGE (no auto-click -> avoids fullscreen)
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!isColorControl(t)) return;
      applyNoFlash(getRootFromTarget(t));
    });

    // Some themes fire this; safe if not
    document.addEventListener('variant:change', (e) => {
      applyNoFlash(getRootFromTarget(e.target));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
